/**
 * Orchestrator MCP server — exposes Appostle-internal tools to a queen agent.
 *
 * The queen runs as a regular Claude agent inside the daemon, but with an
 * extra in-process MCP server registered under the name `appostle`. From her
 * perspective, the tools below appear in her toolbelt as
 * `mcp__appostle__handoff` (and any future tools we add). When she calls one,
 * the SDK pauses her, the handler here runs inside the daemon process, and
 * the result is returned to her conversation as a normal tool result.
 *
 * v1 ships ONE tool: `handoff`. It spawns a full Appostle agent session with
 * a clean context, runs the queen's task on it via the same `agentManager`
 * pipeline the loop / run / ralph runners use, and returns the worker's final
 * assistant text. The session is NOT closed afterwards — the user can
 * Promote-to-session it from the quest panel to inspect the conversation.
 *
 * Notes:
 *   - The MCP server name is exactly `"appostle"`. The Claude SDK auto-prefixes
 *     tool names as `mcp__appostle__<tool>`, so the tool the queen sees is
 *     `mcp__appostle__handoff`.
 *   - Tools are namespaced flatly inside the server (no sub-objects). To add
 *     a new tool later, register another `tool(...)` in the `tools` array.
 *   - Worker spawn config (provider, model, system prompt, allowed tools) is
 *     derived from the role file pointed at by `rolePath`. The runner passes
 *     a callback that reads + parses the role; the MCP server itself does
 *     not touch role files directly — keeps the boundary clean.
 */

import { createSdkMcpServer, tool } from "@anthropic-ai/claude-agent-sdk";
import type { Logger } from "pino";
import { z } from "zod";
import type { AgentManager } from "../agent/agent-manager.js";
import type { AgentSessionConfig } from "../agent/agent-sdk-types.js";
import type { McpSdkServerConfig } from "../agent/agent-sdk-types.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HandoffResult {
  /** Final assistant text the worker emitted before its turn ended. May be empty. */
  finalText: string;
  /** AgentManager id of the spawned worker (so the UI can attach it to a card). */
  agentId: string;
  /** True if the worker was canceled mid-run (e.g. via abort signal). */
  canceled: boolean;
}

/**
 * Function the runner provides to translate a `rolePath` from disk into a full
 * AgentSessionConfig. Centralised in the runner because role parsing semantics
 * (frontmatter, allowed-tools, model) are owned there.
 */
export type ResolveWorkerConfig = (args: {
  rolePath: string;
  taskTitle: string;
}) => Promise<AgentSessionConfig>;

/**
 * Optional callback the runner can register to be notified each time the queen
 * invokes a handoff. Fires AS SOON AS the worker is spawned — used by the
 * runner to stamp the corresponding QuestCard with the worker's `agentId` so
 * the UI can show "running" and let the user promote/inspect it.
 */
export type OnHandoffSpawned = (args: {
  rolePath: string;
  task: string;
  workerAgentId: string;
}) => void;

/**
 * Optional callback the runner can register to be notified when a handoff
 * worker finishes (or errors). Fires AFTER the worker's `runAgent` call
 * returns. Used by the runner to flip the card's status from "running" to
 * "succeeded" / "failed" / "stopped".
 */
export type OnHandoffCompleted = (args: {
  rolePath: string;
  workerAgentId: string;
  status: "succeeded" | "failed" | "stopped";
  errorMessage: string | null;
}) => void;

export interface OrchestratorMcpServerOptions {
  /** Stable id of the parent quest. Used only for log breadcrumbs in v1. */
  questId: string;
  agentManager: AgentManager;
  resolveWorkerConfig: ResolveWorkerConfig;
  onHandoffSpawned?: OnHandoffSpawned;
  onHandoffCompleted?: OnHandoffCompleted;
  logger: Logger;
  /** Aborted when the parent orchestration is stopped — propagates to workers. */
  signal: AbortSignal;
  /**
   * Owner-stamping options forwarded to `agentManager.createAgent` for every
   * worker the queen spawns via handoff. Required (non-null) — single-tenant
   * fallback is gone, every spawned agent must have a resolved owner.
   */
  workerOwnerOptions: {
    ownerUserId: string;
    userId?: string;
    username?: string;
  };
}

// ---------------------------------------------------------------------------
// Tool input schema — exposed to the queen as the `handoff` tool input
// ---------------------------------------------------------------------------

const HANDOFF_INPUT_SHAPE = {
  rolePath: z
    .string()
    .min(1)
    .describe(
      "Absolute filesystem path to the role .md file the worker should adopt as its system prompt. Must be one of the role file paths listed in your team roster — do not invent paths.",
    ),
  task: z
    .string()
    .min(1)
    .describe(
      "Concrete, self-contained instruction for the worker. The worker has its own toolbelt and reads the role at rolePath as its system prompt — write the task as a normal user message describing what to do, what inputs to read, and where to write outputs. Reference the hivemind doc by absolute path if relevant.",
    ),
} as const;

// ---------------------------------------------------------------------------
// Server factory
// ---------------------------------------------------------------------------

export function buildOrchestratorMcpServer(
  options: OrchestratorMcpServerOptions,
): McpSdkServerConfig {
  const {
    questId,
    agentManager,
    resolveWorkerConfig,
    onHandoffSpawned,
    onHandoffCompleted,
    logger,
    signal,
    workerOwnerOptions,
  } = options;

  const handoffTool = tool(
    "handoff",
    [
      "Spawn a fresh full Appostle agent session, give it a role and a task, wait for it to finish, and return its final text.",
      "",
      "Use this tool — and ONLY this tool — to delegate work. Do not attempt to do the work yourself, do not shell out, do not invoke ephemeral subagents.",
      "",
      "Each call spawns one independent session with its own clean context (the whole point: it saves YOUR context budget). To run multiple workers in parallel, emit multiple handoff tool calls in the same response — they will execute concurrently and you will see all the results in the next turn. To run sequentially, wait for one call's result before issuing the next.",
    ].join("\n"),
    HANDOFF_INPUT_SHAPE,
    async (args) => {
      const log = logger.child({ tool: "appostle.handoff", questId });
      log.info({ rolePath: args.rolePath }, "handoff: spawning worker");

      let workerConfig: AgentSessionConfig;
      try {
        workerConfig = await resolveWorkerConfig({
          rolePath: args.rolePath,
          taskTitle: deriveTaskTitle(args.task),
        });
      } catch (err) {
        log.warn({ err }, "handoff: failed to resolve worker config");
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `handoff failed: could not resolve role at "${args.rolePath}" — ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }

      let workerAgentId: string;
      try {
        const created = await agentManager.createAgent(workerConfig, undefined, workerOwnerOptions);
        workerAgentId = created.id;
      } catch (err) {
        log.error({ err }, "handoff: createAgent failed");
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `handoff failed: could not spawn worker — ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }

      onHandoffSpawned?.({
        rolePath: args.rolePath,
        task: args.task,
        workerAgentId,
      });

      // Wire the orchestration's abort signal to this worker so a quest stop
      // cascades cleanly. The cancel call is idempotent if the worker already
      // finished by the time abort fires.
      const onAbort = (): void => {
        void agentManager.cancelAgentRun(workerAgentId).catch(() => {});
      };
      signal.addEventListener("abort", onAbort, { once: true });

      try {
        const run = await agentManager.runAgent(workerAgentId, args.task);
        const finalText = run.finalText.trim();
        if (run.canceled) {
          onHandoffCompleted?.({
            rolePath: args.rolePath,
            workerAgentId,
            status: "stopped",
            errorMessage: null,
          });
          return {
            content: [
              {
                type: "text",
                text: `Worker ${workerAgentId} was canceled before completing.\n\nPartial output (may be empty):\n\n${finalText || "(no output)"}`,
              },
            ],
          };
        }
        onHandoffCompleted?.({
          rolePath: args.rolePath,
          workerAgentId,
          status: "succeeded",
          errorMessage: null,
        });
        return {
          content: [
            {
              type: "text",
              text: finalText
                ? `Worker ${workerAgentId} finished. Final output:\n\n${finalText}`
                : `Worker ${workerAgentId} finished but produced no final assistant text. Inspect the session if you need the full timeline.`,
            },
          ],
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        log.error({ err, workerAgentId }, "handoff: runAgent threw");
        onHandoffCompleted?.({
          rolePath: args.rolePath,
          workerAgentId,
          status: "failed",
          errorMessage,
        });
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `handoff failed mid-run for worker ${workerAgentId}: ${errorMessage}`,
            },
          ],
        };
      } finally {
        signal.removeEventListener("abort", onAbort);
        // Intentionally NOT closing the worker agent — same convention we
        // settled on for the queen. The user must be able to promote-to-
        // session even after a handoff completes; AgentManager owns lifecycle.
      }
    },
  );

  const sdkInstance = createSdkMcpServer({
    name: "appostle",
    version: "0.1.0",
    tools: [handoffTool],
    // Always load — we never want the queen to have to "discover" the handoff
    // tool. Without this it could be deferred behind tool search.
    alwaysLoad: true,
  });

  return {
    type: "sdk",
    name: "appostle",
    config: sdkInstance,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveTaskTitle(task: string): string {
  const firstLine = task.split("\n")[0]?.trim() ?? "";
  if (firstLine.length === 0) return "handoff";
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}…` : firstLine;
}
